import { useState } from 'react';
import { StoryViewer } from './StoryViewer';
import { CreateStoryModal } from './CreateStoryModal';

interface Story {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  userLevel: number;
  stories: {
    id: string;
    type: 'image' | 'achievement';
    imageUrl?: string;
    achievement?: {
      type: 'badge' | 'level' | 'mission';
      title: string;
      icon: string;
    };
    timestamp: Date;
    views: number;
  }[];
  hasViewed: boolean;
}

export function StoriesBar() {
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [showCreateStory, setShowCreateStory] = useState(false);

  const stories: Story[] = [
    {
      id: '1',
      userId: 'current',
      userName: 'Você',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20portrait%20simple%20background%20corporate%20style%20friendly%20smile&width=100&height=100&seq=story-you-001&orientation=squarish',
      userLevel: 12,
      stories: [],
      hasViewed: false
    },
    {
      id: '2',
      userId: '2',
      userName: 'Ana Silva',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20avatar%20portrait%20simple%20background%20corporate%20attire%20confident%20smile&width=100&height=100&seq=story-ana-002&orientation=squarish',
      userLevel: 15,
      stories: [
        {
          id: 's1',
          type: 'achievement',
          achievement: {
            type: 'badge',
            title: 'Mestre das Vendas',
            icon: 'ri-trophy-line'
          },
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
          views: 45
        }
      ],
      hasViewed: false
    },
    {
      id: '3',
      userId: '3',
      userName: 'Carlos Mendes',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20businessman%20avatar%20portrait%20simple%20background%20business%20suit%20friendly%20expression&width=100&height=100&seq=story-carlos-003&orientation=squarish',
      userLevel: 18,
      stories: [
        {
          id: 's2',
          type: 'achievement',
          achievement: {
            type: 'level',
            title: 'Nível 18 Alcançado!',
            icon: 'ri-vip-crown-line'
          },
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
          views: 78
        }
      ],
      hasViewed: true
    },
    {
      id: '4',
      userId: '4',
      userName: 'Beatriz Costa',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20avatar%20portrait%20simple%20background%20elegant%20attire%20warm%20smile&width=100&height=100&seq=story-beatriz-004&orientation=squarish',
      userLevel: 14,
      stories: [
        {
          id: 's3',
          type: 'achievement',
          achievement: {
            type: 'mission',
            title: 'Missão Épica Completa',
            icon: 'ri-flag-line'
          },
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000),
          views: 92
        }
      ],
      hasViewed: false
    },
    {
      id: '5',
      userId: '5',
      userName: 'Diego Oliveira',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20businessman%20avatar%20portrait%20simple%20background%20modern%20business%20casual%20confident&width=100&height=100&seq=story-diego-005&orientation=squarish',
      userLevel: 16,
      stories: [
        {
          id: 's4',
          type: 'achievement',
          achievement: {
            type: 'badge',
            title: 'Campeão do Mês',
            icon: 'ri-medal-line'
          },
          timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000),
          views: 134
        }
      ],
      hasViewed: true
    }
  ];

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {stories.map((story) => (
            <button
              key={story.id}
              onClick={() => {
                if (story.userId === 'current') {
                  setShowCreateStory(true);
                } else {
                  setSelectedStory(story);
                }
              }}
              className="flex-shrink-0 flex flex-col items-center gap-2 group"
            >
              <div className={`relative ${
                story.userId === 'current' 
                  ? 'w-16 h-16 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center border-2 border-dashed border-slate-400 hover:border-teal-500 transition-colors'
                  : story.hasViewed
                    ? 'p-0.5 bg-slate-300 rounded-full'
                    : 'p-0.5 bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-500 rounded-full animate-pulse'
              }`}>
                {story.userId === 'current' ? (
                  <i className="ri-add-line text-2xl text-slate-600 group-hover:text-teal-600 transition-colors"></i>
                ) : (
                  <>
                    <img
                      src={story.userAvatar}
                      alt={story.userName}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-white">
                      {story.userLevel}
                    </div>
                  </>
                )}
              </div>
              <span className={`text-xs font-medium ${
                story.userId === 'current' ? 'text-slate-600' : 'text-slate-700'
              } max-w-[64px] truncate`}>
                {story.userName}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Story Viewer */}
      {selectedStory && (
        <StoryViewer
          story={selectedStory}
          onClose={() => setSelectedStory(null)}
          onNext={() => {
            const currentIndex = stories.findIndex(s => s.id === selectedStory.id);
            const nextStory = stories[currentIndex + 1];
            if (nextStory && nextStory.userId !== 'current') {
              setSelectedStory(nextStory);
            } else {
              setSelectedStory(null);
            }
          }}
          onPrevious={() => {
            const currentIndex = stories.findIndex(s => s.id === selectedStory.id);
            const prevStory = stories[currentIndex - 1];
            if (prevStory && prevStory.userId !== 'current') {
              setSelectedStory(prevStory);
            }
          }}
        />
      )}

      {/* Create Story Modal */}
      {showCreateStory && (
        <CreateStoryModal onClose={() => setShowCreateStory(false)} />
      )}
    </>
  );
}
